import type { ExportJobStatus, ReportColumn, ReportName, ReportResult } from '@rfleet/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Download, FileSpreadsheet, FileText, Play, Printer } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Can } from '../../app/guards';
import { EmptyState, PageHeader, Skeleton, Spinner, Table, Td, Th, useToast } from '../../components/ui';
import { api, qs, type Paginated } from '../../lib/api';
import { fmtDate, fmtDateTime, fmtDuration, fmtEgp, fmtNumber } from '../../lib/format';
import type { DriverRow } from '../drivers/DriversPage';
import type { Group, VehicleRow } from '../vehicles/VehiclesPage';

interface LibraryItem { name: ReportName; titleAr: string; titleEn: string }
interface CustomerLite { id: string; name: string }
const monthStart = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
const today = () => new Date().toISOString().slice(0, 10);

export function ReportsPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const lang = i18n.language;
  const [params, setParams] = useSearchParams();
  const name = (params.get('name') ?? 'trips') as ReportName;
  const [from, setFrom] = useState(params.get('from') ?? monthStart());
  const [to, setTo] = useState(params.get('to') ?? today());
  const [vehicleId, setVehicleId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [driverId, setDriverId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [run, setRun] = useState<{ name: ReportName; qs: string } | null>(null);
  const [job, setJob] = useState<string | null>(null);

  const library = useQuery({ queryKey: ['reports', 'library'], queryFn: () => api<LibraryItem[]>('/reports') });
  const vehicles = useQuery({ queryKey: ['vehicles-lite-all'], queryFn: () => api<Paginated<VehicleRow>>('/vehicles?pageSize=200').then((r) => r.data) });
  const groups = useQuery({ queryKey: ['vehicle-groups'], queryFn: () => api<Group[]>('/vehicle-groups') });
  const drivers = useQuery({ queryKey: ['drivers-lite'], queryFn: () => api<Paginated<DriverRow>>('/drivers?pageSize=200').then((r) => r.data) });
  const customers = useQuery({ queryKey: ['customers-lite'], queryFn: () => api<Paginated<CustomerLite>>('/customers?pageSize=200').then((r) => r.data) });
  const filters = { from, to, vehicleId: vehicleId || undefined, groupId: groupId || undefined, driverId: driverId || undefined, customerId: customerId || undefined };
  const result = useQuery({ queryKey: ['reports', 'run', run], enabled: !!run, queryFn: () => api<ReportResult>(`/reports/${run!.name}${run!.qs}`) });
  const exportReq = useMutation({
    mutationFn: (format: 'xlsx' | 'csv') => api<{ jobId: string }>(`/reports/${name}/export`, { method: 'POST', json: { ...filters, format, lang } }),
    onSuccess: (r) => setJob(r.jobId),
    onError: toast.error,
  });
  const status = useQuery({ queryKey: ['exports', job], enabled: !!job, refetchInterval: (q) => (q.state.data?.state === 'completed' || q.state.data?.state === 'failed' ? false : 1500), queryFn: () => api<ExportJobStatus>(`/exports/${job}`) });
  useEffect(() => {
    if (status.data?.state === 'completed' && status.data.url) {
      window.open(status.data.url, '_blank', 'noopener');
      setJob(null);
    } else if (status.data?.state === 'failed') {
      toast.error(new Error(status.data.error ?? 'export failed'));
      setJob(null);
    }
  }, [status.data, toast]);

  const pick = (n: ReportName) => { setParams((p) => { p.set('name', n); return p; }); setRun(null); };
  const runNow = () => setRun({ name, qs: qs({ ...filters, limit: 1000 }) });
  const r = result.data;
  const title = (i: LibraryItem | undefined) => (i ? (lang === 'en' ? i.titleEn : i.titleAr) : name);
  const current = library.data?.find((i) => i.name === name);

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={t('nav.reports')} subtitle={t('reports.hint')} />
      <div className="grid gap-4 lg:grid-cols-[240px_1fr]">
        <aside className="card flex flex-col gap-1 !p-2 self-start">
          {library.isLoading ? <Skeleton className="h-64" /> : (library.data ?? []).map((i) => (
            <button key={i.name} className={clsx('flex items-center gap-2 rounded-lg px-3 py-2 text-start text-sm font-semibold transition-colors', name === i.name ? 'bg-brand-soft text-brand-ink' : 'text-text-2 hover:bg-surface-hover')} onClick={() => pick(i.name)} aria-current={name === i.name}>
              <FileText size={16} className="flex-none" /> {title(i)}
            </button>
          ))}
        </aside>
        <div className="flex min-w-0 flex-col gap-3">
          <div className="card flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1 text-xs text-text-muted">{t('expenses.from')}<input type="date" dir="ltr" className="input" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
            <label className="flex flex-col gap-1 text-xs text-text-muted">{t('expenses.to')}<input type="date" dir="ltr" className="input" value={to} onChange={(e) => setTo(e.target.value)} /></label>
            {name !== 'invoices' ? <>
              <label className="flex flex-col gap-1 text-xs text-text-muted">{t('vehicles.vehicle')}<select className="input max-w-[170px]" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}><option value="">{t('common.all')}</option>{(vehicles.data ?? []).map((v) => <option key={v.id} value={v.id}>{v.plateAr}</option>)}</select></label>
              <label className="flex flex-col gap-1 text-xs text-text-muted">{t('vehicles.group')}<select className="input max-w-[170px]" value={groupId} onChange={(e) => setGroupId(e.target.value)}><option value="">{t('common.all')}</option>{(groups.data ?? []).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}</select></label>
            </> : null}
            {['trips', 'stops', 'speeding', 'expenses', 'fuel', 'alerts'].includes(name) ? <label className="flex flex-col gap-1 text-xs text-text-muted">{t('vehicles.driver')}<select className="input max-w-[170px]" value={driverId} onChange={(e) => setDriverId(e.target.value)}><option value="">{t('common.all')}</option>{(drivers.data ?? []).map((d) => <option key={d.id} value={d.id}>{d.fullName}</option>)}</select></label> : null}
            {['trips', 'expenses', 'invoices'].includes(name) ? <label className="flex flex-col gap-1 text-xs text-text-muted">{t('trips.customer')}<select className="input max-w-[170px]" value={customerId} onChange={(e) => setCustomerId(e.target.value)}><option value="">{t('common.all')}</option>{(customers.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label> : null}
            <button className="btn-primary" onClick={runNow} disabled={!from || !to || to < from}><Play size={16} /> {t('reports.run')}</button>
            <Can perm="reports:export">
              <div className="ms-auto flex gap-2">
                <button className="btn-secondary" disabled={!!job || exportReq.isPending} onClick={() => exportReq.mutate('xlsx')}>{job ? <Spinner /> : <FileSpreadsheet size={16} />} Excel</button>
                <button className="btn-secondary" disabled={!!job || exportReq.isPending} onClick={() => exportReq.mutate('csv')}><Download size={16} /> CSV</button>
                <button className="btn-secondary" disabled={!r} onClick={() => window.print()}><Printer size={16} /> {t('reports.print')}</button>
              </div>
            </Can>
          </div>
          {job ? <p className="text-xs text-text-muted">{t('reports.exporting')}</p> : null}
          <section className="card print:!border-0 print:!shadow-none">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-base font-bold">{title(current)}</h2>
              {r ? <span className="text-xs text-text-muted">{r.meta.from} → {r.meta.to} · {t('common.totalCount', { count: r.meta.rowCount })}{r.meta.truncated ? ` · ${t('reports.truncated')}` : ''}</span> : null}
            </div>
            {!run ? <EmptyState title={t('reports.pickAndRun')} /> : result.isLoading ? <Skeleton className="h-64" /> : !r?.rows.length ? <EmptyState title={t('common.empty')} /> : (
              <div className="overflow-x-auto">
                <Table>
                  <thead><tr>{r.columns.map((c) => <Th key={c.key} className={clsx(isNumeric(c) && 'text-end')}>{lang === 'en' ? c.labelEn : c.labelAr}</Th>)}</tr></thead>
                  <tbody>
                    {r.rows.map((row, i) => (
                      <tr key={i}>{r.columns.map((c) => <Td key={c.key} className={clsx('whitespace-nowrap', isNumeric(c) && 'text-end tabular-nums')}>{cell(c, row[c.key] ?? null, lang, t)}</Td>)}</tr>
                    ))}
                  </tbody>
                  {Object.keys(r.totals).length ? (
                    <tfoot><tr className="bg-surface-2 font-bold">{r.columns.map((c, i) => <Td key={c.key} className={clsx(isNumeric(c) && 'text-end tabular-nums')}>{c.sum ? cell(c, r.totals[c.key] ?? 0, lang, t) : i === 0 ? t('reports.total') : ''}</Td>)}</tr></tfoot>
                  ) : null}
                </Table>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

const isNumeric = (c: ReportColumn) => ['number', 'money', 'km', 'kph', 'percent', 'duration'].includes(c.type);

function cell(c: ReportColumn, v: string | number | null, lang: string, t: (k: string) => string): string {
  if (v == null || v === '') return '—';
  switch (c.type) {
    case 'money':
      return fmtEgp(v, lang);
    case 'number':
      return fmtNumber(v, lang, Number.isInteger(Number(v)) ? 0 : 2);
    case 'km':
      return `${fmtNumber(v, lang, 1)} ${t('units.km')}`;
    case 'kph':
      return `${fmtNumber(v, lang)} ${t('units.kph')}`;
    case 'percent':
      return `${fmtNumber(v, lang, 1)}%`;
    case 'duration':
      return fmtDuration(Number(v), lang);
    case 'date':
      return fmtDate(String(v), lang);
    case 'datetime':
      return fmtDateTime(String(v), lang);
    default: {
      const s = String(v);
      const key = `reports.values.${s}`;
      const tr = t(key);
      return tr === key ? s : tr;
    }
  }
}
